import React, { useEffect, useState, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AiOutlineSearch, AiOutlineClose } from "react-icons/ai";
import ReactPaginate from "react-paginate";
import { LineWave } from "react-loader-spinner";
import { toast } from "react-toastify";
import StoreLocationCard from "./StoreLocationCard";
import Confirmation from "../../components/confirmation/Confirmation";
import { fetchStoreLocations, deleteStoreLocation } from "../../redux/slices/storeLocationSlice";

const StoreLocations = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { storeLocations, isLoading } = useSelector(
    (state) => state.storeLocationReducer
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("newest");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const isFirstMount = useRef(true);

  // Single effect handles: initial load, navigation back, page change, search, sort
  useEffect(() => {
    const delay = (!isFirstMount.current && (search !== "" || sort !== "newest")) ? 700 : 0;
    isFirstMount.current = false;
    const timer = setTimeout(() => {
      dispatch(fetchStoreLocations({ page: currentPage, limit: 12, search, sort }));
    }, delay);
    return () => clearTimeout(timer);
  }, [currentPage, search, sort, location.key, dispatch]);

  const handlepagination = (e) => {
    const selectedPage = e.selected + 1;
    setCurrentPage(selectedPage);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      const response = await dispatch(deleteStoreLocation(deleteTarget));
      if (deleteStoreLocation.fulfilled.match(response)) {
        toast.success(response?.payload?.message || "Store location deleted successfully");
        setDeleteTarget(null);
        dispatch(fetchStoreLocations({ page: currentPage, limit: 12, search, sort }));
        return;
      }

      toast.error(
        response?.payload?.response?.data?.message ||
        response?.payload?.message ||
        "Failed to delete store location"
      );
    } catch (error) {
      toast.error("Something went wrong while deleting store location");
    }
  };

  const stores = storeLocations?.data || [];
  const totalPages = storeLocations?.meta?.totalPages || storeLocations?.meta?.total_no_search_pages || 1;

  return (
    <div>
      <h1 className="text-left text-white text-3xl font-bold ml-2">
        Store Locations
      </h1>
      <div className="flex items-center justify-between mb-5 pl-3 pr-3 flex-wrap">
        <div className="flex items-center justify-end gap-3 xl:w-full w-full xl:pt-0 pt-5">
          <div className="relative xl:w-1/2">
            <button
              type="button"
              className="absolute left-3 top-[50%] -translate-y-1/2"
            >
              <AiOutlineSearch className="text-gray-400" />
            </button>
            <input
              type="text"
              className="bg-[#372F5B] h-10 px-5 pr-10 pl-[35px] text-white rounded-full text-sm focus:outline-none w-full"
              placeholder="Search by store name or location"
              onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
              value={search}
            />
            {search && (
              <button
                type="button"
                className="absolute right-3 top-[50%] -translate-y-1/2 text-gray-400 hover:text-white"
                onClick={() => { setSearch(""); setCurrentPage(1); }}
                aria-label="Clear search"
              >
                <AiOutlineClose />
              </button>
            )}
          </div>
          <div className="text-right pr-2">
            <Link
              to="/panel/addlocation"
              className="bg-red-500 text-white font-bold py-2 px-4 rounded inline-block"
            >
              Add Store
            </Link>
          </div>
          <div>
            <select
              value={sort}
              onChange={(e) => { setSort(e.target.value); setCurrentPage(1); }}
              className="bg-[#372F5B] text-white text-sm rounded-md px-3 py-2 focus:outline-none h-10"
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
            </select>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-stretch">
        {stores.length === 0 && !isLoading ? (
          <div className="text-center text-gray-500 py-8 text-3xl w-full">
            No Store Locations Found
          </div>
        ) : (
          stores.map((store) => (
            <StoreLocationCard key={store._id} data={store} onDelete={(id) => setDeleteTarget(id)} />
          ))
        )}
      </div>

      {stores.length > 0 && totalPages > 1 && (
        <div className="flex justify-end py-4">
          <ReactPaginate
            previousLabel="Previous"
            nextLabel="Next"
            onPageChange={handlepagination}
            breakLabel="..."
            breakClassName="mx-2 text-white"
            pageCount={totalPages}
            marginPagesDisplayed={2}
            pageRangeDisplayed={5}
            containerClassName="flex justify-center"
            previousClassName="px-4 py-2 bg-gray-800 text-white rounded-l cursor-pointer"
            nextClassName="px-4 py-2 bg-gray-800 text-white rounded-r cursor-pointer"
            activeClassName="px-4 py-2 bg-red-500 text-white"
            pageClassName="px-4 py-2 bg-[#372F5B] text-white"
            forcePage={currentPage - 1}
          />
        </div>
      )}

      {isLoading && (
        <div className="loader">
          <LineWave color={"#fff"} loading={isLoading} size={150} />
        </div>
      )}

      {deleteTarget && (
        <Confirmation
          message="delete this store location"
          buttonText="Delete"
          confirmDelete={handleDelete}
          cancelDelete={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
};

export default StoreLocations;
